import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, BarChart3, BookMarked, BookOpen, Camera,
  CheckCircle2, ChevronRight, Disc3, FileText, Info, Layers, LifeBuoy, Package,
  Search, Settings2, ShieldCheck, Ship, Truck, User, Zap,
} from 'lucide-react';

type SectionId = 'intro' | 'auth' | 'interface' | 'modes' | 'cargo' | 'twins' |
  'securing' | 'calcmodes' | 'scenarios' | 'results' | 'pdf' | 'surveys' |
  'steelcoil' | 'projects' | 'profile' | 'faq';

type ManualSection = {
  id: SectionId;
  title: string;
  navLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  content: React.ReactNode;
};

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-3 leading-relaxed text-slate-600 dark:text-slate-300">{children}</p>
);

const H3 = ({ children }: { children: React.ReactNode }) => (
  <h3 className="mb-3 mt-6 text-lg font-bold text-slate-800 dark:text-slate-100">{children}</h3>
);

const List = ({ items }: { items: React.ReactNode[] }) => (
  <ul className="mb-4 space-y-1.5">
    {items.map((item, index) => (
      <li key={index} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Table = ({ headers, rows }: { headers: string[]; rows: string[][] }) => (
  <div className="my-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-400/20">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="bg-slate-100 text-left dark:bg-[#202c42]">
          {headers.map((header) => (
            <th key={header} className="whitespace-nowrap px-4 py-3 font-bold text-slate-700 dark:text-slate-200">{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex} className={rowIndex % 2 ? 'bg-slate-50/60 dark:bg-white/[0.025]' : 'bg-white dark:bg-transparent'}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex} className="border-t border-slate-100 px-4 py-2.5 text-slate-700 dark:border-slate-400/10 dark:text-slate-300">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Notice = ({ type = 'info', children }: { type?: 'info' | 'tip' | 'warning' | 'important'; children: React.ReactNode }) => {
  const styles = {
    info: ['bg-blue-50 dark:bg-blue-950/30', 'border-blue-300 dark:border-blue-700', Info, 'text-blue-600'],
    tip: ['bg-emerald-50 dark:bg-emerald-950/30', 'border-emerald-300 dark:border-emerald-700', CheckCircle2, 'text-emerald-600'],
    warning: ['bg-amber-50 dark:bg-amber-950/30', 'border-amber-300 dark:border-amber-700', AlertTriangle, 'text-amber-600'],
    important: ['bg-orange-50 dark:bg-orange-950/30', 'border-orange-300 dark:border-orange-700', ShieldCheck, 'text-orange-600'],
  }[type];
  const Icon = styles[2] as React.ComponentType<{ className?: string }>;
  return (
    <div className={`my-4 flex gap-3 rounded-xl border p-4 ${styles[0]} ${styles[1]}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${styles[3]}`} />
      <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">{children}</div>
    </div>
  );
};

const Shot = ({ src, alt, caption }: { src: string; alt: string; caption: string }) => (
  <figure className="my-6">
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-lg dark:border-slate-400/20 dark:bg-black/20">
      <img src={src} alt={alt} className="w-full object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
    </div>
    <figcaption className="mt-2 text-center text-xs italic text-slate-500 dark:text-slate-400">{caption}</figcaption>
  </figure>
);

const sections: ManualSection[] = [
  {
    id: 'intro', navLabel: '1. Giriş', title: '1. Giriş ve Sisteme Genel Bakış', icon: BookOpen,
    content: <>
      <Notice type="important"><strong>Hesaplama yöntemi:</strong> Bu platform, IMO/ILO/UNECE CTU Code 2014 ve ilgili Informative Material’dan yararlanan <strong>basitleştirilmiş bir yük sabitleme değerlendirme yöntemi</strong> kullanır. EN 12195-1, IMO CSS Code Annex 13 veya UIC kurallarına uygunluk sertifikası değildir. Sonuçlar yalnız girilen veriler ve belirtilen varsayımlar için geçerlidir; taşıma öncesinde yetkili yük sabitleme uzmanı, sörveyör ve ilgili taşıyıcı tarafından doğrulanmalıdır.</Notice>
      <P><strong>Lashing Calculator SaaS Platformu</strong>; kara, demiryolu ve deniz taşımalarında yük sabitleme gereksinimlerini hesaplayan profesyonel bir mühendislik aracıdır. Kuvvet hesabı, sabitleme elemanı optimizasyonu, 3D görselleştirme, PDF raporlama ve fotoğraflı saha kontrolü iş akışlarını tek yerde toplar.</P>
      <H3>Uçtan Uca Temel İş Akışı</H3>
      <Table headers={['Adım', 'Yapılacak İş', 'Kontrol Noktası']} rows={[
        ['1', 'Taşıma modunu seçin', 'Yükün yol, demiryolu ve deniz ayaklarının tamamını kapsayan modu kullanın'],
        ['2', 'Taşıma ünitesini seçin', 'Ünitenin kapasitesini, iç ölçülerini ve bağlantı noktalarını doğrulayın'],
        ['3', 'Yük bilgilerini girin', 'Brüt ağırlık, uzunluk, genişlik ve yükseklik gerçek değerlerle girilmelidir'],
        ['4', 'Ağırlık merkezini girin', 'Üretici CoG belgesi varsa geometrik merkez varsayımı yerine belgelenen değerleri kullanın'],
        ['5', 'Sürtünme yüzeyini seçin', 'Kuru/temiz temas yüzeyi ile sahadaki gerçek koşul aynı olmalıdır'],
        ['6', 'Sabitleme elemanlarını yapılandırın', 'Doğrudan bağlama, Loop Lashing, takoz veya stoper uygunluğunu kontrol edin'],
        ['7', 'Hesabı çalıştırın', 'Dört yönün kayma ve devrilme sonuçlarını ayrı ayrı değerlendirin'],
        ['8', '3D düzeni ve PDF raporu inceleyin', 'Son düzen saha uygulamasıyla eşleşmeli ve yetkin uzman tarafından doğrulanmalıdır'],
      ]} />
      <H3>Kimler İçin?</H3>
      <Table headers={['Kullanıcı', 'Temel Kullanım']} rows={[
        ['Deniz ve yük sörveyörleri', 'Sahadaki bağlama planını hesaplanan gereksinimlerle karşılaştırmak'],
        ['Yük sabitleme firmaları', 'Kara ve deniz taşımaları için düzenli bağlama planları hazırlamak'],
        ['Liman terminal işletmeleri', 'Yük sabitleme kontrol listelerini standartlaştırmak'],
        ['Freight forwarder ekipleri', 'Rezervasyon öncesinde sabitleme planını kontrol etmek'],
        ['Sigorta uzmanları', 'Zaman damgalı hesap kayıtlarını incelemek'],
      ]} />
    </>,
  },
  {
    id: 'auth', navLabel: '2. Giriş ve Kimlik Doğrulama', title: '2. Başlangıç ve Kimlik Doğrulama', icon: ShieldCheck,
    content: <>
      <P><code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-orange-600 dark:bg-white/10">/login</code> adresine gidin ve kayıtlı e-posta adresiniz ile parolanızı girin.</P>
      <Shot src="/manual/01_login_page.png" alt="Giriş sayfası" caption="Kurumsal e-posta adresi ve parola ile giriş ekranı" />
      <Notice><strong>Çoklu Oturum Koruması</strong> nedeniyle hesap aynı anda ikinci bir cihazda açılırsa önceki oturum güvenlik amacıyla sonlandırılır.</Notice>
      <H3>Güvenli Oturum Kullanımı</H3>
      <List items={[
        'Kurumsal hesabınızı ve parolanızı başka kullanıcılarla paylaşmayın.',
        'Ortak kullanılan bilgisayarlarda çalışma bittiğinde Çıkış düğmesini kullanın.',
        'Oturum beklenmedik biçimde kapanırsa hesabın başka bir cihazda açılıp açılmadığını kontrol edin.',
        'Kullanıcı adı ve parola ekran görüntülerinde veya PDF raporlarında yer almamalıdır.',
      ]} />
      <H3>Kullanıcı Rolleri</H3>
      <Table headers={['Rol', 'Erişim']} rows={[
        ['Standart kullanıcı', 'Hesap makinesi, raporlar, profil ve kullanıcı kılavuzu'],
        ['Sörveyör', 'Sörveyör paneli ve saha raporu düzenleyicisi'],
        ['Yönetici', 'Tüm özellikler, yönetim paneli ve gelişmiş bağlama ekipmanı görünümü'],
      ]} />
    </>,
  },
  {
    id: 'interface', navLabel: '3. Arayüz Turu', title: '3. Hesaplama Arayüzü', icon: Layers,
    content: <>
      <Shot src="/manual/tr/01_calculator_overview.png" alt="Yapılandırılmış hesaplama ekranı" caption="Taşıma modu, taşıma ünitesi, yük ölçüleri ve sabitleme elemanları girilmiş çalışma alanı" />
      <H3>Üst Menü</H3>
      <List items={[
        <><strong>Dil seçimi:</strong> İngilizce, Türkçe, İspanyolca, Almanca, Çince, Japonca ve Korece</>,
        <><strong>Tema düğmesi:</strong> Açık ve koyu görünüm arasında geçiş</>,
        <><strong>Kullanıcı Kılavuzu:</strong> Bu yardım sayfasını açar</>,
        <><strong>Raporlar:</strong> Kaydedilmiş PDF raporlarını açar</>,
        <><strong>Profil:</strong> Hesap ve kullanım bilgilerini gösterir</>,
        <><strong>Çıkış:</strong> Oturumu güvenli şekilde kapatır</>,
      ]} />
      <H3>Ana Çalışma Alanları</H3>
      <Table headers={['Alan', 'İçerik']} rows={[
        ['Sabitleme paneli', 'Hesaplama modu, halat seçimi, takoz, stoper ve Hesapla düğmesi'],
        ['Yük bilgileri', 'Taşıma modu, taşıma ünitesi, ağırlık, ölçüler, ağırlık merkezi ve sürtünme yüzeyi'],
        ['Sonuçlar ve 3D görünüm', 'Kuvvet tablosu, 3D ağırlık merkezi ve 3D bağlama diyagramı'],
      ]} />
      <H3>Form Davranışları</H3>
      <List items={[
        <><strong>Bağımlı seçimler:</strong> Taşıma ünitesi listesi, taşıma modu seçildikten sonra kullanılabilir ve seçilen moda göre filtrelenir.</>,
        <><strong>Sonuç sıfırlama:</strong> Ağırlık, ölçü, CoG, sürtünme veya sabitleme elemanı değiştirildiğinde eski sonuçlar temizlenir. Bu, güncel olmayan PDF veya tablo kullanılmasını önler.</>,
        <><strong>Sayısal alanlar:</strong> Değeri doğrudan yazabilir veya eksi/artı düğmelerini kullanabilirsiniz.</>,
        <><strong>Turuncu bilgi simgeleri:</strong> CoG alanlarının açıklamasını ve devrilme hesabındaki önemini gösterir.</>,
        <><strong>Koyu tema:</strong> Tüm seçim alanları, açılır seçenekler ve sonuç tabloları yüksek kontrastla okunabilir biçimde tasarlanmıştır.</>,
      ]} />
    </>,
  },
  {
    id: 'modes', navLabel: '4. Taşıma Modları', title: '4. Taşıma Modları ve Taşıma Üniteleri', icon: Ship,
    content: <>
      <P>Yükün gerçek taşıma rotasına uygun modu seçin. Seçilen mod, kuvvet hesabında kullanılan ivme katsayılarını belirler.</P>
      <Table headers={['Mod', 'Açıklama', 'İleri', 'Geri', 'Yanal']} rows={[
        ['Road', 'Yalnızca karayolu', '0,8 g', '0,5 g', '0,5 g'], ['Rail', 'Demiryolu ve manevra kuvvetleri', '1,0 g', '1,0 g', '0,5 g'],
        ['Sea A', 'Korunaklı ve sakin sular', '0,3 g', '0,3 g', '0,5 g'], ['Sea B', 'Normal deniz koşulları', '0,3 g', '0,3 g', '0,7 g'],
        ['Sea C', 'Şiddetli deniz koşulları', '0,4 g', '0,4 g', '0,8 g'], ['Road + Sea B', 'Karayolu ve ardından denizyolu', '0,8 g', '0,5 g', '0,7 g'],
      ]} />
      <Notice type="tip">Çok modlu taşımalarda tüm taşıma ayaklarının kuvvetlerini kapsayan birleşik modu seçin.</Notice>
      <H3>Doğru Seçim Sırası</H3>
      <List items={[
        'Önce Taşıma Modu alanını seçin. Bu seçim yapılmadan Taşıma Ünitesi alanı bilinçli olarak kısıtlıdır.',
        'İkinci olarak Taşıma Ünitesi alanını seçin. Konteyner, Open Top, Flatrack ve Diğer/Özel gruplarını açarak seçenekleri görebilirsiniz.',
        'Son olarak Sürtünme Yüzeyi alanını seçin. Taşıma modu veya ünitesi daha sonra değiştirilirse uyumsuz alt seçimler otomatik sıfırlanabilir.',
      ]} />
      <H3>Taşıma Üniteleri</H3>
      <P>Taşıma ünitesi listesi seçilen moda göre otomatik filtrelenir. Örneğin Mafi yalnızca uygun deniz taşımalarında, stoper ise Vessel veya Mafi ünitelerinde kullanılabilir.</P>
      <Table headers={['Ünite', 'Tipik Kullanım']} rows={[
        ['20 / 40 ft Container', 'Konteyner içi standart yükler'], ['20 / 40 ft Flatrack', 'Ağır veya gabari dışı yükler'],
        ['Breakbulk', 'Büyük ve ağır proje yükleri'], ['Lowbed / Truck Trailer', 'Karayolu taşımaları'],
        ['Mafi Roll Trailer', 'Ro-Ro deniz taşımaları'], ['Vessel Hold', 'Gemi ambarı veya güverte uygulamaları'],
      ]} />
    </>,
  },
  {
    id: 'cargo', navLabel: '5. Yük ve Ağırlık Merkezi', title: '5. Yük Bilgileri ve Ağırlık Merkezi', icon: Package,
    content: <>
      <H3>5.1 — Ağırlık ve Ölçüler</H3>
      <Table headers={['Alan', 'Açıklama', 'Birim']} rows={[
        ['Brüt ağırlık', 'Toplam yük kütlesi', 'Metrik ton'], ['Uzunluk', 'İleri-geri eksendeki yük uzunluğu', 'metre'],
        ['Genişlik', 'Yanal eksendeki yük genişliği', 'metre'], ['Yükseklik', 'Temas yüzeyinden itibaren yük yüksekliği', 'metre'],
      ]} />
      <H3>5.2 — Ağırlık Merkezi Koordinatları</H3>
      <Table headers={['Alan', 'Açıklama']} rows={[
        ['CoG Length', 'Ağırlık merkezinin yükün uzunluğu boyunca konumu'], ['CoG Width', 'Ağırlık merkezinin yük genişliği boyunca konumu'],
        ['CoG Height', 'Ağırlık merkezinin temas yüzeyinden yüksekliği; devrilme hesabı için kritik değerdir'],
      ]} />
      <Shot src="/manual/tr/03_cog_centered_3d.png" alt="Merkezde ağırlık merkezi" caption="Geometrik merkezde bulunan CoG: uzunluk, genişlik ve yükseklik koordinatları yük sınırları içindedir" />
      <Notice type="tip">Homojen dikdörtgen yüklerde CoG Height genellikle yük yüksekliğinin yarısıdır. Makine ve özel yüklerde üretici ağırlık merkezi belgesini kullanın.</Notice>
      <H3>5.3 — CoG Edge Case’leri</H3>
      <Table headers={['Durum', 'Etkisi', 'Yapılması Gereken']} rows={[
        ['Yüksek CoG', 'Devrilme momentini ve üstten bağlama ihtiyacını artırır', 'Üretici verisini doğrulayın; daha fazla veya daha güçlü doğrudan bağlama değerlendirin'],
        ['Uzunluk yönünde merkez dışı CoG', 'İleri ve geri yönlerin devrilme/kayma dengesi farklılaşır', 'CoG Length değerini ölçüm referansına göre doğru girin'],
        ['Genişlik yönünde merkez dışı CoG', 'Sağ ve sol yön sonuçları asimetrik olabilir', 'Yanal bağlama dağılımını iki yönde ayrı inceleyin'],
        ['CoG yük sınırının dışında', 'Fiziksel olarak geçersiz veya yanlış referanslı veri anlamına gelir', 'Koordinat başlangıç noktasını ve birimleri kontrol edin'],
        ['CoG bilinmiyor', 'Geometrik merkez varsayımı gerçek makine için iyimser olabilir', 'Üreticiden CoG belgesi isteyin; belirsizliği uzman değerlendirmesine ekleyin'],
      ]} />
      <Shot src="/manual/tr/04_cog_high_edge_case.png" alt="Yüksek CoG" caption="Yüksek CoG edge case’i: kırmızı işaret yükün üst bölgesine yaklaşan ağırlık merkezini gösterir" />
      <Shot src="/manual/tr/05_cog_off_center_edge_case.png" alt="Merkez dışı CoG" caption="Uzunluk ve genişlik yönlerinde merkez dışına taşınmış CoG örneği" />
      <H3>5.4 — Sürtünme Yüzeyi</H3>
      <P>Yük ile zemin arasındaki gerçek temas malzemesini seçin. Yanlış veya iyimser sürtünme katsayısı, gerekli bağlama miktarını olduğundan düşük gösterebilir.</P>
      <Notice type="warning">Yağ, su, buz, pas, kir, boya veya hasarlı anti-slip mat sürtünme davranışını değiştirebilir. Uygulamadaki malzeme adı tek başına saha koşullarının uygunluğunu garanti etmez.</Notice>
    </>,
  },
  {
    id: 'twins', navLabel: '6. 3D Görselleştirmeler', title: '6. 3D Ağırlık Merkezi ve Bağlama Diyagramı', icon: BarChart3,
    content: <>
      <H3>6.1 — 3D Ağırlık Merkezi</H3>
      <P>3D ağırlık merkezi görünümü, değerleri girerken canlı olarak güncellenir. Yük kutusunu ve ağırlık merkezinin yük içindeki konumunu gösterir.</P>
      <Shot src="/manual/tr/03_cog_centered_3d.png" alt="3D ağırlık merkezi" caption="Yük geometrisi, yön eksenleri ve ağırlık merkezinin üç boyutlu görünümü" />
      <List items={['Modeli döndürmek için sürükleyin', 'Yakınlaştırma kontrolleriyle ayrıntıları inceleyin', 'Yüksek veya merkez dışı CoG değerlerinde devrilme riskini ayrıca değerlendirin']} />
      <H3>6.2 — 3D Bağlama Düzeni</H3>
      <P>Hesaplamadan sonra doğrudan bağlamalar, loop lashings, takozlar ve stoperler üç boyutlu yerleşim üzerinde gösterilir.</P>
      <Shot src="/manual/tr/07_3d_lashing_results.png" alt="3D bağlama diyagramı" caption="Hesaplanan doğrudan bağlama, Loop Lashing ve destek elemanlarının 3D yerleşimi" />
      <Notice type="important">3D görünüm bir yerleşim ve iletişim aracıdır. Halatın gerçek bağlantı noktası, keskin kenar koruması, temas engelleri ve saha erişilebilirliği ayrıca kontrol edilmelidir.</Notice>
    </>,
  },
  {
    id: 'securing', navLabel: '7. Sabitleme Elemanları', title: '7. Sabitleme Elemanlarının Yapılandırılması', icon: Settings2,
    content: <>
      <H3>7.1 — Doğrudan Bağlama</H3><P>Halatlar yük bağlantı noktalarından taşıma ünitesindeki bağlama noktalarına çapraz bağlanır. Optimizer modunda sistem gerekli minimum adedi belirler; Calculator modunda tür, adet, açı ve konumu kullanıcı girer.</P>
      <List items={[
        'Yük ve taşıma ünitesi bağlantı noktalarının gerçek MSL kapasitesini doğrulayın.',
        'Halatın seçilen yön bileşenine katkı sağlayacak geometride olduğundan emin olun.',
        'Keskin köşelerde uygun kenar koruyucu kullanın ve halatın bükülme/ezilme riskini kontrol edin.',
        'Manuel modda her yön için halat türü, adet, açı ve CoG üstü/altı konumu ayrı girilir.',
      ]} />
      <H3>7.2 — Loop Lashing</H3><P>Yükün üzerinden dolaşan bağlama, düşey ön germe yoluyla sürtünme katkısı sağlar.</P>
      <Notice type="warning">Yük genişliği taşıma ünitesini aşıyorsa Loop Lashing otomatik kapatılır; taşan yüklerde bu yöntem fiziksel olarak uygun değildir.</Notice>
      <List items={[
        'Loop Lashing kutusunu etkinleştirin ve açılan listeden halat/webbing türünü seçin.',
        'Optimizer modunda sistem gerekli düzeni hesaplar; Calculator modunda her yön için adet girilir.',
        'Ön germe kuvveti, kenar koruması ve yük üst yüzeyinin basınca dayanımı sahada doğrulanmalıdır.',
      ]} />
      <H3>7.3 — Ahşap Takoz</H3><List items={['Wooden Dunnage seçeneğini etkinleştirin', 'Takoz yüksekliği, genişliği ve yön bazındaki adetleri girin', 'Vessel dışındaki uygun taşıma ünitelerinde kullanılır']} />
      <Shot src="/manual/tr/02_loop_lashing_and_dunnage.png" alt="Loop Lashing ve ahşap takoz" caption="Loop Lashing halat seçimi ile yön bazında ahşap takoz boyut ve adet alanları" />
      <Notice type="important">Takoz hesabı, takozun fiziksel olarak doğru sabitlendiğini varsayar. Ahşap kalitesi, lif yönü, temas alanı, çivileme/kaynaklı destek ve zemin dayanımı uzman tarafından doğrulanmalıdır.</Notice>
      <H3>7.4 — Çelik Stoper</H3><P>Stoper adetlerini ileri, geri, sağ ve sol yönler için ayrı ayrı girin. Stoperler yalnızca uygun Sea modu ile Vessel veya Mafi ünitelerinde kullanılabilir.</P>
      <Shot src="/manual/tr/09_vessel_stopper_configuration.png" alt="Vessel stoper yapılandırması" caption="Sea C ve Gemi taşıma ünitesinde dört yön için stoper adetlerinin girilmesi" />
      <H3>7.5 — Gabari Dışı Genişlik</H3>
      <P>Flatrack iç genişliğini aşan yüklerde sağ ve sol takozların fiziksel yerleşimi mümkün olmayabilir. Sistem bu alanları sıfırlar ve devre dışı bırakır.</P>
      <Shot src="/manual/tr/11_oversized_width_dunnage_edge_case.png" alt="Gabari dışı genişlik" caption="2,8 m genişliğindeki flatrack yükünde sağ/sol takozların otomatik devre dışı kalması" />
    </>,
  },
  {
    id: 'calcmodes', navLabel: '8. Optimizer ve Calculator', title: '8. Hesaplama Modları', icon: Zap,
    content: <>
      <H3>Optimizer</H3><P>Dört yöndeki güvenlik dengesini sağlayacak bağlama düzenini otomatik olarak hesaplar.</P>
      <H3>Calculator (Manuel)</H3><P>Sahada kullanılacak bağlama türünü, adedini, açısını ve ağırlık merkezine göre konumunu kullanıcı belirler; sistem düzeni kontrol eder.</P>
      <H3>Optimizer’dan Manuel Moda Aktarma</H3>
      <List items={['Optimizer ile ilk hesabı çalıştırın', 'Calculator moduna geçin ve sonuçları manuel alanlara aktarın', 'Saha ekipmanına göre tür ve adetleri düzenleyin', 'Tekrar hesaplayarak yeni düzenin sonuçlarını kontrol edin']} />
      <Shot src="/manual/tr/08_manual_direct_and_loop.png" alt="Manuel bağlama düzeni" caption="Optimizer sonucundan manuel moda aktarılan Loop Lashing ve yön bazında doğrudan bağlama alanları" />
      <Notice type="warning">Manuel moda geçtikten sonra girilen her değişiklik sonuçları geçersiz kılar. Yeni düzeni PDF’e aktarmadan önce Hesapla düğmesini tekrar kullanın.</Notice>
    </>,
  },
  {
    id: 'scenarios', navLabel: '9. Kullanım Senaryoları', title: '9. Adım Adım Kullanım Senaryoları', icon: Truck,
    content: <>
      <H3>Senaryo 1 — Standart Karayolu Taşıması</H3>
      <Table headers={['Parametre', 'Örnek Değer']} rows={[
        ['Taşıma modu', 'Road'], ['Taşıma ünitesi', 'Truck Trailer Standard'], ['Yük ağırlığı', '15 MT'],
        ['Ölçüler', '3,0 × 2,0 × 2,0 m'], ['Sürtünme', 'Metal / Wood'], ['Hesaplama modu', 'Optimizer'],
      ]} />
      <Shot src="/manual/tr/06_optimizer_results.png" alt="Örnek sonuçlar" caption="Dört yönün kayma ve devrilme güvenlik durumlarını gösteren optimizer sonucu" />
      <H3>Senaryo 2 — Sea C ve Çelik Stoper</H3><P>Sea C ve Vessel seçin. Yük bilgilerini girdikten sonra stoper adetlerini yönlere göre tanımlayın, uygun halatları seçin ve hesabı çalıştırın.</P>
      <H3>Senaryo 3 — Gabari Dışı Flatrack Yükü</H3><P>Flatrack ve uygun deniz modunu seçin. Yük genişliği sınırı aşıyorsa gabari dışı uyarısını dikkate alın; Loop Lashing kapatılır ve flatrack yerleşimi için ek yönlendirme gösterilir.</P>
      <H3>Senaryo 4 — Breakbulk Boyut Aşımı</H3>
      <P>Break Bulk seçildiğinde flatrack tipi, yük pozisyonu, flatrackler arası mesafe ve önerilen flatrack sayısı alanları açılır. Yük boyu seçilen flatrack boyuna eşit veya büyükse sistem hesaplamayı durdurur.</P>
      <Shot src="/manual/tr/10_breakbulk_dimension_edge_case.png" alt="Breakbulk boyut aşımı" caption="Seçilen 20 ft flatrack boyunu aşan 10 m yük için engelleyici doğrulama uyarısı" />
    </>,
  },
  {
    id: 'results', navLabel: '10. Sonuçların Okunması', title: '10. Hesaplama Sonuçlarının Yorumlanması', icon: CheckCircle2,
    content: <>
      <Table headers={['Sütun', 'Açıklama']} rows={[
        ['Sliding', 'Yükü ilgili yönde kaydırmaya çalışan atalet kuvveti'], ['Tipping', 'Yük kenarı çevresindeki devrilme etkisi'],
        ['Friction', 'Ağırlık ve sürtünme katsayısından gelen direnç'], ['Securing Element', 'Takoz veya stoper direnci'],
        ['Loop Lashing', 'Loop bağlamadan gelen katkı'], ['Direct Lashing', 'Doğrudan bağlamalardan gelen direnç'],
        ['Net Sliding / Net Tipping', 'Kalan emniyet dengesi; sonuç tablosundaki durum göstergeleriyle birlikte değerlendirilir'],
      ]} />
      <Shot src="/manual/tr/06_optimizer_results.png" alt="Sonuç tablosu" caption="İleri, geri, sağ ve sol yönlerde kayma ve devrilme güvenlik faktörleri" />
      <H3>Sonuçları Okuma Sırası</H3>
      <List items={[
        'Her yönü ayrı değerlendirin; bir yöndeki başarılı sonuç diğer yönleri otomatik olarak güvenli yapmaz.',
        'Net Kayma ve Net Devrilme başlıklarının altındaki güvenlik faktörü ile durum göstergesini birlikte okuyun.',
        'Optimizer’ın önerdiği halat türü ve adetlerini Malzeme Kullanımı/PDF bölümünden doğrulayın.',
        'Sonucun Geçti olması; bağlantı noktası, halat kondisyonu, ön germe, kenar koruması ve saha uygulamasını otomatik doğrulamaz.',
      ]} />
      <Notice type="important">Yeşil sonuç, girilen veriler ve varsayımlar kapsamında hesabın geçtiğini gösterir. Saha uygulamasının uygunluğu yine yetkin uzman tarafından doğrulanmalıdır.</Notice>
    </>,
  },
  {
    id: 'pdf', navLabel: '11. PDF Raporları', title: '11. PDF Raporları ve Arşiv', icon: FileText,
    content: <>
      <P>Başarılı hesaplamadan sonra sol paneldeki <strong>Download PDF</strong> düğmesine basın. Rapor; proje açıklamasını, yük ve taşıma bilgilerini, kullanılan katsayıları, sonuç tablosunu, sabitleme malzemelerini ve diyagramları içerir.</P>
      <List items={['İsteğe bağlı proje açıklamasını girin', 'Download düğmesiyle raporu oluşturun', 'Geçmiş raporlara My PDF Reports alanından yeniden erişin']} />
      <Notice>PDF raporları hesaplama destek dokümanıdır; uygulama öncesinde yetkin uzman doğrulaması gerektirir.</Notice>
      <Shot src="/manual/tr/12_pdf_project_description.png" alt="PDF proje açıklaması" caption="PDF’e eklenecek kısa ve tanımlayıcı proje açıklaması" />
      <Shot src="/manual/tr/13_pdf_report_viewer.png" alt="PDF rapor önizleme" caption="Hesaplama parametreleri, malzeme kullanımı ve doğrulama tablosunu içeren PDF raporu" />
      <Shot src="/manual/tr/14_saved_pdf_reports.png" alt="PDF rapor geçmişi" caption="Kaydedilmiş raporları görüntüleme, indirme ve silme seçenekleri" />
    </>,
  },
  {
    id: 'surveys', navLabel: '12. Saha Kontrolü ve Fotoğraflar', title: '12. Saha Kontrolü ve Fotoğraf Modülü', icon: Camera,
    content: <>
      <P>Saha ekibi, kendisine gönderilen özel bağlantıyı hesap açmadan kullanarak yükleme ve bağlama fotoğraflarını yükleyebilir.</P>
      <H3>Misafir Yükleme Bağlantısı</H3>
      <List items={['Raporlar alanında ilgili kaydı bulun', 'Copy Link ile özel bağlantıyı kopyalayın', 'Bağlantıyı saha ekibine WhatsApp, SMS veya e-posta ile gönderin', 'Yüklenen fotoğrafları fotoğraflı PDF raporunda kullanın']} />
      <Shot src="/manual/tr/16_field_reports_and_photo_workflow.png" alt="Saha raporları" caption="Saha raporu, fotoğraf bekleme durumu, paylaşım bağlantısı ve Summary PDF seçenekleri" />
      <H3>Fotoğraf Kalitesi Kontrolü</H3>
      <List items={[
        'Genel görünüm fotoğrafı yükün tamamını ve taşıma ünitesini göstermelidir.',
        'Dört köşe fotoğrafında bağlama noktaları ve halat güzergâhı net görünmelidir.',
        'Etiket/plaka fotoğrafı yük kimliği ve varsa ağırlık bilgisi okunacak kalitede olmalıdır.',
        'Karanlık, bulanık veya bağlantı noktasını kesen fotoğraflar yeniden çekilmelidir.',
        'Fotoğraflı PDF, hesaplama anındaki plan ile sahadaki gerçek uygulamanın karşılaştırılması için kullanılmalıdır.',
      ]} />
      <P>iPhone HEIC fotoğrafları yükleme öncesinde JPEG’e dönüştürülür; büyük fotoğraflar güvenilir mobil aktarım için otomatik sıkıştırılır.</P>
    </>,
  },
  {
    id: 'steelcoil', navLabel: '13. Steel Coil (Sac Rulo) Modülü', title: '13. Steel Coil (Sac Rulo) Yük Sabitleme Modülü', icon: Disc3,
    content: <>
      <P>Sac rulo (steel coil) taşımalarında CTU Code 2014 ve IMO MSC.1/Circ.1497 standartlarına uygun beşik (bedding), takoz mukavemeti, temas açıları ve lashing bağlama hesaplamaları gerçekleştirebilirsiniz.</P>
      <H3>Modül Özellikleri ve Kullanım Adımları</H3>
      <List items={[
        'Rulo Boyutları & Ağırlığı: Sac rulo dış çapı, iç çapı, genişliği ve toplam ağırlığını tanımlayın.',
        'Yerleşim Yönü (Roll Orientation): Rulonun gözü gidiş yönüne paralel (Eye-to-Side / Eye-to-Front) veya yan yatık konumunu seçin.',
        'Bedding & Takoz Geometrisi: Ahşap beşik (bedding) boyutlarını, takoz açısını (35°-45°) ve takoz yüksekliğini girin.',
        'Yapısal Kesit Mukavemeti: Çelik ve ahşap takozların kayma ve devrilmeye karşı dayanım kapasitelerini doğrulayın.',
        '2D SVG & 3D Visualizer: Canlı 3D Three.js sahnede beşik, takoz ve halat geometrisini inceleyin.',
        '2 Sayfalı Özel PDF Raporu: Adım adım hesap izi ve teknik izometrik çizimleri içeren raporu indirin.'
      ]} />
      <Notice type="important">Sac rulo hesaplamalarında temas yüzeyi sürtünme katsayısı ve takoz takma açısı devrilme güvenlik katsayısını doğrudan etkiler. Özel uzman incelemesi önerilir.</Notice>
    </>,
  },
  {
    id: 'projects', navLabel: '14. Doğrulamalar ve Edge Case’ler', title: '14. Doğrulama Kuralları ve Edge Case Yönetimi', icon: BookMarked,
    content: <>
      <Table headers={['Edge Case', 'Sistem Davranışı', 'Kullanıcı Aksiyonu']} rows={[
        ['Taşıma modu seçilmedi', 'Taşıma ünitesi seçimi kısıtlanır ve hesaplama başlamaz', 'Önce yükün tüm taşıma ayaklarını kapsayan modu seçin'],
        ['Taşıma ünitesi moda uygun değil', 'Uyumsuz ünite listede gösterilmez veya seçim sıfırlanır', 'Mod değişikliğinden sonra üniteyi yeniden doğrulayın'],
        ['Ağırlık veya ölçü sıfır', 'Zorunlu alan doğrulaması hesaplamayı durdurur', 'Pozitif ve doğru birimlerde değer girin'],
        ['CoG fiziksel sınırların dışında', 'Girdi geçersiz kabul edilir veya sonuç güvenilir olmaz', 'Referans başlangıcını, ekseni ve birimi kontrol edin'],
        ['Flatrack genişliği aşıldı', 'Sağ/sol takozlar devre dışı kalır; Loop Lashing uygun olmayabilir', 'Gabari dışı yerleşim ve doğrudan bağlamayı değerlendirin'],
        ['Breakbulk boyu flatrack boyunu aşıyor', 'Kırmızı uyarı gösterilir ve hesaplama engellenir', 'Flatrack tipini, yönünü veya adetini düzeltin'],
        ['Stoper seçildi ancak adet girilmedi', 'Hesaplama stoper doğrulamasında durur', 'En az bir yönde uygulamaya uygun stoper adedi girin'],
        ['Manuel modda halat türü/adedi eksik', 'İlgili yönde bağlama katkısı oluşmaz veya doğrulama hatası gösterilir', 'Her aktif satırda halat türü ve pozitif adet kullanın'],
        ['Hesaptan sonra giriş değişti', 'Eski sonuç ve PDF düğmesi temizlenir', 'Yeni değerlerle hesabı yeniden çalıştırın'],
      ]} />
      <Notice type="important">Hata mesajını atlayarak eski sonuç ekranını kullanmayın. Girişlerle sonuçların aynı hesaplama çalıştırmasına ait olduğundan emin olun.</Notice>
    </>,
  },
  {
    id: 'profile', navLabel: '14. Profil ve Ayarlar', title: '14. Profil, Ayarlar ve Hesap Yönetimi', icon: User,
    content: <>
      <Shot src="/manual/tr/15_profile_and_usage.png" alt="Profil sayfası" caption="Hesaplama/PDF kredileri, bağlama gösterim formatı, PDF teması ve şirket profili" />
      <List items={['Hesap e-postası ve kayıt bilgileri', 'Hesaplama kullanım geçmişi', 'PDF rapor arşivi', 'Uygun planlarda kurumsal PDF için şirket logosu yükleme']} />
      <H3>Dil ve Tema</H3><P>Üst menüden açık/koyu tema arasında geçiş yapabilir ve uygulama dilini değiştirebilirsiniz. Türkçe seçildiğinde bu kılavuz Türkçe; diğer dillerde İngilizce görüntülenir.</P>
    </>,
  },
  {
    id: 'faq', navLabel: 'Sık Sorulan Sorular', title: 'Sık Sorulan Sorular ve Sorun Giderme', icon: LifeBuoy,
    content: <div className="space-y-4">
      {[
        ['Hesapla düğmesi neden pasif?', 'Zorunlu alanlardan biri eksik veya geçersiz olabilir. Ağırlık, ölçüler, taşıma modu, taşıma ünitesi ve halat seçimini kontrol edin.'],
        ['Loop Lashing neden kullanılamıyor?', 'Yük genişliği taşıma ünitesini aşıyor olabilir. Gabari dışı yüklerde Loop Lashing otomatik kapatılır.'],
        ['Stoper seçeneği neden görünmüyor?', 'Stoper yalnızca uygun Sea modu ile Vessel veya Mafi taşıma ünitelerinde gösterilir.'],
        ['Wooden Dunnage neden görünmüyor?', 'Ahşap takoz Vessel taşıma ünitesinde kullanılamaz; uygun durumda stoper kullanın.'],
        ['Sonuçlar neden kırmızı?', 'Daha güçlü veya daha fazla halat kullanın; uygun ise Loop Lashing, takoz veya stoper ekleyin ve tekrar hesaplayın.'],
        ['3D görünüm neden siyah veya boş?', 'Donanım hızlandırması açık güncel bir Chromium tabanlı tarayıcı kullanmayı deneyin.'],
        ['Taşıma ünitesinde yalnızca bazı seçenekler neden görünüyor?', 'Taşıma ünitesi seçenekleri seçilen taşıma moduna göre filtrelenir. Özel üniteler için ilgili açılır menü grubunu genişletin.'],
        ['Değer değiştirdikten sonra sonuçlarım neden kayboldu?', 'Eski sonuçların yeni girişlerle karışmasını önlemek için her giriş değişikliği sonuçları bilinçli olarak sıfırlar. Yeniden hesaplayın.'],
        ['Sağ ve sol takoz alanları neden sıfır ve pasif?', 'Yük genişliği flatrack sınırını aştığında yanal takozların fiziksel olarak uygulanamayacağı kabul edilir.'],
        ['Optimizer sonucu sahadaki halatlarımla uyuşmuyor.', 'Hesaplayıcıda Düzenle ile manuel moda geçin, mevcut ekipmanı girin ve düzeni tekrar hesaplayın.'],
        ['CoG belgem yoksa ne yapmalıyım?', 'Geometrik merkez yalnızca homojen yüklerde makul bir varsayımdır. Ağır makine ve asimetrik yüklerde üreticiden CoG belgesi isteyin.'],
        ['PDF raporuna şirket logosu nasıl eklenir?', 'Bu özellik uygun abonelik planlarında Profil içindeki şirket ayarlarından kullanılabilir.'],
      ].map(([question, answer]) => (
        <details key={question} className="group overflow-hidden rounded-xl border border-slate-200 dark:border-slate-400/20">
          <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 font-semibold text-slate-800 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5">
            <span>{question}</span><ChevronRight className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-90" />
          </summary>
          <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-sm text-slate-600 dark:border-slate-400/10 dark:text-slate-300">{answer}</div>
        </details>
      ))}
    </div>,
  },
];

const UserManualTurkish: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState<SectionId>('intro');

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveSection(entry.target.id as SectionId);
      });
    }, { rootMargin: '-30% 0px -60% 0px' });
    document.querySelectorAll('section[id]').forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const visibleSections = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase('tr-TR');
    if (!query) return sections;
    return sections.filter((section) =>
      `${section.navLabel} ${section.title}`.toLocaleLowerCase('tr-TR').includes(query),
    );
  }, [searchQuery]);

  const scrollTo = (id: SectionId) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 transition-colors dark:bg-[#111827] dark:text-white">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-md dark:border-slate-400/20 dark:bg-[#182235]/95 lg:px-8">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <button onClick={() => navigate(-1)} className="rounded-xl p-2 text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5" title="Geri">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="rounded-xl bg-orange-600/10 p-2.5 text-orange-600"><BookOpen className="h-6 w-6" /></div>
          <div>
            <h1 className="text-lg font-black tracking-tight">Kullanıcı Kılavuzu ve Operasyon Rehberi</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">CTU Code (IMO/ILO/UNECE) | Sürüm 2.4.2</p>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-8 lg:flex-row lg:px-8">
        <aside className="w-full shrink-0 lg:w-64 xl:w-72">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-400/20 dark:bg-[#182235]">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Bölümlerde ara…" className="w-full rounded-xl border border-transparent bg-slate-100 py-2 pl-9 pr-3 text-sm outline-none transition-all focus:border-orange-500 dark:bg-[#26344d] dark:text-white" />
            </div>
            <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">İçindekiler</p>
            <nav className="max-h-[70vh] space-y-0.5 overflow-y-auto">
              {visibleSections.map(({ id, icon: Icon, navLabel }) => (
                <button key={id} onClick={() => scrollTo(id)} className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-semibold transition-all ${activeSection === id ? 'bg-orange-50 text-orange-600 dark:bg-orange-600/10 dark:text-orange-400' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5'}`}>
                  <Icon className="h-3.5 w-3.5 shrink-0" />{navLabel}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="min-w-0 flex-1 space-y-16 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-400/20 dark:bg-[#182235] lg:p-10">
          {sections.map(({ id, title, icon: Icon, content }) => (
            <section id={id} key={id} className="scroll-mt-28">
              <h2 className="mb-5 flex items-center gap-3 border-b border-slate-100 pb-3 text-2xl font-black text-slate-900 dark:border-slate-400/15 dark:text-white">
                <span className="rounded-xl bg-orange-600/10 p-2 text-orange-600"><Icon className="h-6 w-6" /></span>{title}
              </h2>
              {content}
            </section>
          ))}
          <footer className="border-t border-slate-100 pt-8 text-center text-xs text-slate-400 dark:border-slate-400/15">
            <p>Lashing Calculator Kullanıcı Kılavuzu — Sürüm 2.4.2</p>
            <p className="mt-1">© 2026 LashingPlan.com | CTU Code (IMO/ILO/UNECE) Basitleştirilmiş Değerlendirme Yöntemi</p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default UserManualTurkish;
